import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import {
    EXECUTION_STATUS,
    REPORT_COLUMNS,
    STAGE_LABEL,
    errorEntries,
    executionStatus,
    failureOf,
    hasErrorInfo,
    latestStatus,
    reportSummary,
} from '../../helpers/reports'
import { RADIUS, STATUS, SURFACE, TEXT } from '../../theme/tokens'

/** Rows per page, as in the old comms app. */
export const RESULTS_PAGE_SIZE = 100

/** Execution Status text colours (the text-safe STATUS foregrounds); each is tested as text on the card in both themes in theme/contrast.test.js. */
export const EXECUTION_STATUS_COLOR = {
    [EXECUTION_STATUS.SUCCESS]: STATUS.success.fg,
    [EXECUTION_STATUS.PENDING]: STATUS.warning.fg,
    [EXECUTION_STATUS.FAILURE]: STATUS.error.fg,
}

const cellSx = {
    borderColor: SURFACE.border,
    color: TEXT.secondary,
    whiteSpace: 'nowrap',
}

/** The Reason cell wraps: a reason is a sentence, not a value. */
const reasonCellSx = {
    ...cellSx,
    whiteSpace: 'normal',
    minWidth: 280,
    maxWidth: 440,
}

/**
 * What a report came to: how many comms succeeded, are pending or failed,
 * and the failure reasons with how many comms each one hit, most common
 * first. For the NPS sends of 24 Oct this is the one line the team needed
 * ("9 failed: ... this template still sends through Meta ...").
 */
const Summary = ({ rows }) => {
    const summary = reportSummary(rows)
    const tiles = [
        { label: 'Comm logs', value: summary.total, color: TEXT.primary },
        {
            label: 'Succeeded',
            value: summary.success,
            color: STATUS.success.fg,
        },
        { label: 'Pending', value: summary.pending, color: STATUS.warning.fg },
        { label: 'Failed', value: summary.failure, color: STATUS.error.fg },
    ]
    return (
        <Box data-testid="report-summary" sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {tiles.map((tile) => (
                    <Box
                        key={tile.label}
                        sx={{
                            px: 2,
                            py: 1.25,
                            minWidth: 128,
                            border: `1px solid ${SURFACE.border}`,
                            borderRadius: `${RADIUS.md}px`,
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{ color: TEXT.faint, display: 'block' }}
                        >
                            {tile.label}
                        </Typography>
                        <Typography
                            variant="h2"
                            component="p"
                            sx={{
                                lineHeight: 1.2,
                                color: tile.color,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {tile.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {summary.reasons.length > 0 && (
                <Box
                    component="ul"
                    aria-label="Failure reasons"
                    sx={{
                        m: 0,
                        pl: 0,
                        listStyle: 'none',
                        display: 'grid',
                        gap: 0.75,
                    }}
                >
                    {summary.reasons.map(({ reason, count }) => (
                        <Typography
                            component="li"
                            key={reason}
                            variant="body2"
                            sx={{ color: TEXT.secondary }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    color: STATUS.error.fg,
                                    fontWeight: 600,
                                }}
                            >
                                {count} failed
                            </Box>
                            : {reason}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    )
}

Summary.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
}

/**
 * Why one comm failed: the stage, the reason in plain words, the provider's
 * code and message, then, for a delivery failure, each of the provider's
 * errors with its code, title, message, details and link (the old app's
 * Error Info dialog). Until 26 Oct 2026 the dialog read only the delivery
 * errors, so a refused send (the NPS WhatsApp sends through Meta) said "The
 * provider gave no error details".
 */
const ErrorInfoDialog = ({ row, onClose }) => {
    const failure = failureOf(row)
    const errors = errorEntries(row)
    const line = (label, value) =>
        value !== '' && value !== null && value !== undefined ? (
            <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                <Box component="span" sx={{ color: TEXT.muted }}>
                    {label}:
                </Box>{' '}
                {value}
            </Typography>
        ) : null
    return (
        <Dialog
            open
            onClose={onClose}
            aria-labelledby="error-info-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="error-info-title">Error info</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 0.5 }}>
                {line('Status', latestStatus(row))}
                {failure && (
                    <>
                        {line(
                            'Failed while',
                            STAGE_LABEL[failure.stage] || failure.stage
                        )}
                        {line('Reason', failure.reason)}
                        {line('Code', failure.code)}
                        {failure.message !== failure.reason &&
                            line("Provider's message", failure.message)}
                    </>
                )}
                {!failure && errors.length === 0 && (
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.muted, mt: 1.5 }}
                    >
                        The provider gave no error details.
                    </Typography>
                )}
                {errors.map((e, i) => (
                    <Box
                        key={`${e.code}-${i}`}
                        data-testid="error-entry"
                        sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: `1px solid ${SURFACE.border}`,
                            display: 'grid',
                            gap: 0.5,
                        }}
                    >
                        {line('Code', e.code)}
                        {line('Title', e.title)}
                        {line('Message', e.message)}
                        {line('Details', e.details)}
                        {e.link && (
                            <Typography variant="body2">
                                <Link
                                    href={e.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    More about this error
                                </Link>
                            </Typography>
                        )}
                    </Box>
                ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ErrorInfoDialog.propTypes = {
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
}

/**
 * The comm logs a small report returned: a summary of their outcomes, then
 * the old comms app's table (its thirteen columns plus Reason, 100 rows a
 * page, scrolling sideways inside the card). Error Info opens for every comm
 * that failed, at whichever stage.
 *
 * @param {object[]} rows       comm log rows (never empty: the page says
 *                              "No comm logs match" itself)
 * @param {function} onDownload saves these rows as the report file
 */
const ReportResults = ({ rows, onDownload }) => {
    const [page, setPage] = useState(0)
    const [errorRow, setErrorRow] = useState(null)
    useEffect(() => setPage(0), [rows])

    const visible = rows.slice(
        page * RESULTS_PAGE_SIZE,
        (page + 1) * RESULTS_PAGE_SIZE
    )

    return (
        <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>
            <Summary rows={rows} />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                    {rows.length} comm log{rows.length === 1 ? '' : 's'}. Times
                    are India time.
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={onDownload}
                >
                    Download these as a file
                </Button>
            </Box>
            <TableContainer
                data-testid="report-results-scroll"
                sx={{ overflowX: 'auto' }}
            >
                <Table size="small" sx={{ minWidth: 2200 }}>
                    <TableHead>
                        <TableRow>
                            {REPORT_COLUMNS.map((c) => (
                                <TableCell key={c.key} sx={cellSx}>
                                    <Typography variant="overline">
                                        {c.label}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map((row, i) => (
                            <TableRow
                                key={row.execution_id || `${page}-${i}`}
                                data-testid="report-row"
                            >
                                {REPORT_COLUMNS.map((c) => {
                                    if (c.key === 'error_info')
                                        return (
                                            <TableCell key={c.key} sx={cellSx}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={
                                                        !hasErrorInfo(row)
                                                    }
                                                    onClick={() =>
                                                        setErrorRow(row)
                                                    }
                                                >
                                                    Error Info
                                                </Button>
                                            </TableCell>
                                        )
                                    if (c.key === 'reason')
                                        return (
                                            <TableCell
                                                key={c.key}
                                                data-testid="reason-cell"
                                                sx={reasonCellSx}
                                            >
                                                {c.value(row)}
                                            </TableCell>
                                        )
                                    if (c.key === 'execution_status') {
                                        const status = executionStatus(row)
                                        return (
                                            <TableCell
                                                key={c.key}
                                                sx={{
                                                    ...cellSx,
                                                    color: EXECUTION_STATUS_COLOR[
                                                        status
                                                    ],
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {status}
                                            </TableCell>
                                        )
                                    }
                                    return (
                                        <TableCell key={c.key} sx={cellSx}>
                                            {c.value(row) ?? ''}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > RESULTS_PAGE_SIZE && (
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={RESULTS_PAGE_SIZE}
                    rowsPerPageOptions={[RESULTS_PAGE_SIZE]}
                />
            )}
            {errorRow && (
                <ErrorInfoDialog
                    row={errorRow}
                    onClose={() => setErrorRow(null)}
                />
            )}
        </Box>
    )
}

ReportResults.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    onDownload: PropTypes.func.isRequired,
}

export default ReportResults
